/**
 * Delete Bank Account Dialog Component
 * 
 * Confirmation dialog for deleting bank account.
 */

import * as React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";
import type { BankAccount } from "@/services/bankAccountService";

interface DeleteBankAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: BankAccount | null;
  onConfirm: () => Promise<void>;
}

export function DeleteBankAccountDialog({
  open,
  onOpenChange,
  account,
  onConfirm,
}: DeleteBankAccountDialogProps) {
  const [deleting, setDeleting] = React.useState(false);

  const handleConfirm = async () => {
    if (!account) return;

    setDeleting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      console.error("Error deleting bank account:", error);
    } finally {
      setDeleting(false);
    }
  };

  if (!account) return null;

  const getBankName = (bank: string | { _id: string; name: string; code: string }) => {
    if (typeof bank === "string") {
      return bank;
    }
    return bank.name;
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Hapus Rekening?</AlertDialogTitle>
          <AlertDialogDescription>
            Apakah Anda yakin ingin menghapus rekening <strong>{getBankName(account.bank)}</strong> - <strong>{account.accountNumber}</strong> ({account.accountName})?
            Tindakan ini tidak dapat dibatalkan.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Batal</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700"
          >
            {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Hapus
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

